import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../utils/api';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, failed, expired
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [topupCredits, setTopupCredits] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('failed');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('failed');
      return;
    }

    try {
      const res = await api.get(`/payments/status/${sessionId}`);
      const data = res.data;
      
      setPaymentDetails(data);

      if (data.payment_status === 'paid') {
        // If this was an AI Generations top-up, credit the generations now.
        if (data.metadata?.type === 'ai_generations') {
          try {
            const res2 = await api.post(`/ai/add-credits/${sessionId}`);
            setTopupCredits(res2.data?.credits_added ?? (Number(data.metadata?.credits) || null));
          } catch (e) {
            // Idempotent endpoint: ignore "already added" / non-AI sessions
          }
        }
        setStatus('success');
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      if (attempts < maxAttempts - 1) {
        setTimeout(pollPaymentStatus, pollInterval);
      } else {
        setStatus('failed');
      }
    }
  };

  const formatAmount = (cents) => {
    return (cents / 100).toFixed(2);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
        {status === 'loading' && (
          <>
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Processing Payment
            </h1>
            <p className="text-slate-600 mb-6">
              Please wait while we confirm your payment...
            </p>
            <div className="flex justify-center">
              <div className="flex gap-1">
                {[...Array(3)].map((_, i) => (
                  <div 
                    key={i}
                    className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce"
                    style={{ animationDelay: `${i * 0.15}s` }}
                  />
                ))}
              </div>
            </div>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Payment Successful!
            </h1>
            <p className="text-slate-600 mb-6">
              {topupCredits
                ? `${Number(topupCredits).toLocaleString()} AI Generations have been added to your account.`
                : 'Thank you for your purchase. Your subscription is now active.'}
            </p>
            
            {paymentDetails && (
              <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
                <h3 className="font-semibold text-slate-900 mb-3">Order Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Plan</span>
                    <span className="font-medium text-slate-900">
                      {paymentDetails.metadata?.tier_name || paymentDetails.metadata?.plan_name || 'Subscription'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Billing</span>
                    <span className="font-medium text-slate-900 capitalize">
                      {paymentDetails.metadata?.billing_period || 'Monthly'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Amount</span>
                    <span className="font-medium text-slate-900">
                      ${formatAmount(paymentDetails.amount_total)} {paymentDetails.currency?.toUpperCase()}
                    </span>
                  </div>
                  {paymentDetails.metadata?.reseller_brand && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">Provided by</span>
                      <span className="font-medium text-slate-900">
                        {paymentDetails.metadata.reseller_brand}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-3">
              <Button asChild className="w-full">
                <Link to="/login">
                  Go to Dashboard
                  <ArrowRight className="ml-2" size={18} />
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full">
                <Link to="/">
                  Back to Home
                </Link>
              </Button>
            </div>
          </>
        )}

        {status === 'failed' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Payment Failed
            </h1>
            <p className="text-slate-600 mb-6">
              We couldn't process your payment. Please try again or contact support if the issue persists.
            </p>
            <div className="space-y-3">
              <Button asChild className="w-full">
                <Link to="/#pricing">
                  Try Again
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full">
                <Link to="/">
                  Back to Home
                </Link>
              </Button>
            </div>
          </>
        )}

        {status === 'expired' && (
          <>
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Session Expired
            </h1>
            <p className="text-slate-600 mb-6">
              Your payment session has expired. Please start a new checkout.
            </p>
            <div className="space-y-3">
              <Button asChild className="w-full">
                <Link to="/#pricing">
                  View Plans
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full">
                <Link to="/">
                  Back to Home
                </Link>
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
